'use client';

import { FC, useCallback, useEffect, useState } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { GeneralInformation } from './GeneralInformation';
import { Services } from './Services';
import { Experience } from './Experience';
import { Education } from './Education/Education'

import {
  CompanyNode,
  TagNode,
  QualificationNode,
  UniversityNode,
} from 'generated/types';
import { useRouter, useSearchParams } from 'next/navigation';
import { ProfileEditHeader } from 'app/(authenticated)/(member)/my-profile/_components/ProfileEditHeader';
import { LoadCitiesMutation, UserQuery } from 'generated/operations';

type EditUserNode = NonNullable<UserQuery['user']>;

interface IProfileForm {
  user: EditUserNode;
  cities: NonNullable<LoadCitiesMutation['loadCities']>['cities'];
  hobbies: TagNode[];
  skills: TagNode[];
  companies: CompanyNode[];
  qualifications: QualificationNode[];
  universities: UniversityNode[];
}

export enum TabIds {
  GENERAL = 'general',
  SERVICES = 'services',
  EXPERIENCE = 'experience',
  EDUCATION = 'education',
}

interface IFormTabs {
  label: string;
  slug: TabIds;
}

export const formTabs: IFormTabs[] = [
  {
    label: 'General',
    slug: TabIds.GENERAL
  },
  {
    label: 'Hashtags',
    slug: TabIds.SERVICES
  },
  {
    label: 'Experiences',
    slug: TabIds.EXPERIENCE,
  },
  {
    label: 'Education',
    slug: TabIds.EDUCATION,
  },
];

const buildUrlParams = (slug: TabIds) => {
  return `?tab=${slug}`;
};

export const ProfileForm: FC<IProfileForm> = (props) => {
  const { cities, companies, hobbies, skills, user, qualifications, universities } = props;

  const params = useSearchParams();
  const router = useRouter();

  const [savedUser, setUser] = useState<EditUserNode>(user);

  const [dirtyForms, setDirtyForms] = useState<TabIds[]>([]);
  const [currentTab, setTab] = useState<TabIds>(() => {
    if (params?.get('tab')) {
      const f = formTabs.find(item => item.slug === params?.get('tab')) ?? formTabs[0];
      return f.slug;
    }

    return TabIds.GENERAL;
  });

  const handleSetDirtyForms = useCallback((formName: TabIds, isDirty: boolean, user?: EditUserNode) => {
    if (dirtyForms.includes(formName) && !isDirty) {
      setDirtyForms((prevState) => prevState.filter(item => item !== formName));
    } else if (!dirtyForms.includes(formName) && isDirty) {
      setDirtyForms((prevState) => [...prevState, formName]);
    }

    if (user) {
      setUser(() => user);
    }
  }, [dirtyForms]);

  const handleSetTab = useCallback((value: string | TabIds) => {
    setTab(() => value as TabIds);

    const newUrl = buildUrlParams(value as TabIds);
    window.history.replaceState({}, '', newUrl);
  }, [router]);

  const handleUpdateTab = useCallback((value: string | TabIds) => {
    if (dirtyForms.includes(currentTab)) {
      const promptValue = confirm('Are you sure you want to leave this tab? All unsaved data will be lost.');

      if (!promptValue) {
        // setTab(() => currentTab);
        return;
      }
    }

    handleSetTab(value);

  }, [currentTab, dirtyForms, handleSetTab]);

  const handleUpdateUser = useCallback((userData: EditUserNode) => {
    setUser({ ...userData });
  }, [])

  useEffect(() => {
    if (params?.get('tab')) {
      const f = formTabs.find(item => item.slug === params?.get('tab')) ?? formTabs[0];
      setTab(f.slug as TabIds);
    }
  }, [params]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if (dirtyForms.length) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dirtyForms.length]);

  useEffect(() => {
    if (params?.get('tab') === undefined || params?.get('tab') === null) {
      router.replace(`?tab=${currentTab}`)
    }
  }, [currentTab, params, router]);

  const onFinish = useCallback(() => {
    if (dirtyForms.length) {
      return confirm('Are you sure you want to finish? All unsaved data will be lost.');
    }
    return true
  }, [dirtyForms.length]);

  return (
    <>
      <ProfileEditHeader onFinish={onFinish} userId={user.id} />
      <div className="md:px-4">
        <Tabs
          value={currentTab}
          defaultValue={currentTab}
          activationMode="manual"
          className="flex flex-col gap-10 w-full"
          onValueChange={handleUpdateTab}
        >
          <TabsList className="flex gap-3 flex-wrap border-0 justify-start">
            {formTabs.map(item => (
              <TabsTrigger
                value={item.slug}
                key={item.slug}
                className={`${dirtyForms.includes(item.slug) ? 'after:absolute after:translate-x-1/3 after:content-[""] after:w-2 after:h-2 after:rounded-full after:bg-warning-500 after:top-0 after:right-0' : ''}`}
                onClick={() => handleUpdateTab(item.slug)}
              >
                {item.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={'general'}>
            <GeneralInformation
              currentTab={currentTab}
              user={savedUser}
              cities={cities}
              onSetDirtyForm={handleSetDirtyForms}
              onUpdateUser={handleUpdateUser}
            />
          </TabsContent>
          <TabsContent value={'services'}>
            <Services
              currentTab={currentTab}
              user={savedUser}
              hobbies={hobbies}
              skills={skills}
              onSetDirtyForm={handleSetDirtyForms}
              onUpdateUser={handleUpdateUser}
            />
          </TabsContent>
          <TabsContent value={'experience'}>
            <Experience
              companies={companies}
              currentTab={currentTab}
              user={savedUser}
              onSetDirtyForm={handleSetDirtyForms}
              onUpdateUser={handleUpdateUser}
            />
          </TabsContent>
          <TabsContent value={'education'}>
            <Education
              universities={universities}
              qualifications={qualifications}
              currentTab={currentTab}
              user={savedUser}
              onSetDirtyForm={handleSetDirtyForms}
              onUpdateUser={handleUpdateUser}
            />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};
